import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Spinner } from '../uikit';
import { formatLastUpdated } from '../lib/relativeTime';

export interface ScanIndicatorProps {
  /** How many of the client's OWN add-source scans currently have the
   * visible spinner showing (Figma 3142:29000) - SourcesContext's
   * `scanningSourceCount`. `0` renders the idle "Last updated" state
   * instead; any positive count renders "Scanning N source(s)".
   *
   * Whole-branch review fix wave (Critical 2 + 3): this used to be a plain
   * `scanning: boolean` plus an unused `sourceCount` that was never anything
   * but 1 in practice (the single-scan model could only ever track one add
   * at a time). Now that concurrent adds are tracked as a set
   * (addSourceScan.ts), this single prop carries the real count - Figma's
   * own copy is "Scanning N sources", so two concurrent adds now genuinely
   * render "Scanning 2 sources" instead of a hardcoded singular. */
  scanningCount: number;
  /** The idle state's timestamp (Figma 2871:49860) - the feed's own
   * `lastUpdatedAt` (AllArticlesPage.tsx's `state.lastUpdatedAt`). `null`
   * renders nothing at all - a brand-new project with no articles yet has
   * nothing to report, and Figma has no third state for that case. */
  lastUpdatedAt: string | null;
}

// How often the idle label's relative wording re-renders (~60s, per the
// COM-640 Task 2 brief) - "3 hours ago" would otherwise silently go stale on
// a page left open, since nothing else re-renders this component on its own.
const RELATIVE_LABEL_REFRESH_MS = 60_000;

// COM-640: right-aligned on the All Articles heading row (AllArticlesPage.tsx)
// - `Scanning N source(s)` + spinner while the user's own add is scanning
// (Figma 3142:29000), `Last updated <relative>` otherwise (Figma
// 2871:49860). Both states share one `data-testid` with a `data-state`
// discriminant so e2e can assert on BEHAVIOUR (which state is showing), not
// on colour or exact wording.
export function ScanIndicator({ scanningCount, lastUpdatedAt }: ScanIndicatorProps) {
  // Forces a re-render every ~60s purely so `formatLastUpdated` recomputes
  // against a fresh `Date.now()` below - the tick's own value is never read.
  const [, forceTick] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => forceTick((tick) => tick + 1), RELATIVE_LABEL_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, []);

  if (scanningCount > 0) {
    return (
      <Box data-testid="scan-indicator" data-state="scanning" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Spinner sx={{ width: 16, height: 16 }} />
        <Typography fontSize={14} fontWeight={500} color="text.secondary">
          Scanning {scanningCount} source{scanningCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    );
  }

  const label = formatLastUpdated(lastUpdatedAt);
  if (!label) {
    return null;
  }

  return (
    <Typography data-testid="scan-indicator" data-state="idle" fontSize={14} fontWeight={500} color="text.secondary">
      {label}
    </Typography>
  );
}
